/**
 * Displays product availability, estimated delivery times, and inventory details.
 * This component is a migration of the legacy `Disponibilidad.tsx`.
 */
import { ExtendedCatalogCard } from '@/components/blocks/ProductDetails/parts/ExtendedCatalogCard';
import { getAvailabilityState } from '@/components/blocks/ProductDetails/utils/utils';
import { TYPES } from '@/data/constants/product';
import { useInventoryV2 } from '@/data/Content/InventoryV2';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { usePhysicalStoreData } from '@/data/Content/usePhysicalStoreData';
import { ContentContext } from '@/data/context/content';
import { isB2BStore, useSettings } from '@/data/Settings';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import {
	getBayData,
	useDefaultStoreInventoryData as buildDefaultStoreInventoryData,
} from '@/utils/inventoryUtils';
import { isKitOrBundleType } from '@/utils/productIsA';
import { FC, useContext, useMemo } from 'react';
import { fulfillmentTypes, PRODUCT } from 'shared/custom/constants/commonConstants';
import {
	INVENTORY_SEARCH_DB,
	INVENTORY_SEARCH_PDP,
} from 'shared/custom/constants/inventoryConstants';
import { StoreAvailabilityCard } from './StoreAvailabilityCard';

export const ProductAvailability: FC = () => {
	const { product: baseProduct, selection } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const product = baseProduct?.type === PRODUCT && selection?.sku ? selection?.sku : baseProduct;
	const { settings } = useSettings();

	const productIdsForInventory = useMemo(() => {
		switch (product?.type) {
			case TYPES.sku:
				return product?.id;
			case TYPES.product:
				return product?.items?.map((item: any) => item?.id).join(',');
			case TYPES.kit:
				return product?.components?.map((item: any) => item?.uniqueID).join(',');
			default:
				return undefined;
		}
	}, [product]);

	const { physicalStoreId, uniqueID } = useStoreIDS();

	const { stLocName: locationName, storeName } = usePhysicalStoreData({
		physicalStoreId,
	});

	const inventorySearchType =
		product?.type === TYPES.sku || product?.type === TYPES.kit
			? INVENTORY_SEARCH_PDP
			: INVENTORY_SEARCH_DB;
	const { availability: inventoryData } = useInventoryV2({
		productIds: productIdsForInventory,
		physicalStoreIds: [uniqueID],
		search: inventorySearchType,
	});

	const isB2B = isB2BStore(settings);
	const attributes = product?.attributes;
	const isKitOrBundle = isKitOrBundleType[product?.type as string];
	const kitComponents = useMemo(
		() => (isKitOrBundle ? product?.components ?? [] : []),
		[isKitOrBundle, product?.components]
	);

	const marketInventory = useMemo(
		() =>
			inventoryData ?? [
				buildDefaultStoreInventoryData(
					physicalStoreId,
					product?.partNumber ?? '',
					product?.id ?? '',
					physicalStoreId,
					INVENTORY_SEARCH_PDP
				),
			],
		[inventoryData, physicalStoreId, product?.partNumber, product?.id]
	);

	const derivedState = useMemo(
		() =>
			getAvailabilityState({
				product,
				marketInventory,
				physicalStoreId,
				isKitOrBundle,
				kitComponents,
			}),
		[product, marketInventory, physicalStoreId, isKitOrBundle, kitComponents]
	);

	if (!product) {
		return null;
	}

	const {
		fulfillmentType,
		hideStoreInventory,
		storeAvailability,
		storeAvailProduct,
		markInventory,
	} = derivedState;

	const bayData = getBayData(product, physicalStoreId);

	const showSearchInOtherStore =
		fulfillmentType === fulfillmentTypes.ONLINE_ONLY || fulfillmentType === fulfillmentTypes.STORE;

	const showExtendCatalog =
		fulfillmentType === fulfillmentTypes.DROPSHIP ||
		fulfillmentType === fulfillmentTypes.SPECIAL_ORDER ||
		attributes?.some((attr) => attr?.identifier === 'ONLINE_ONLY_SELLING_STORE');

	if (markInventory === 0) return null;

	return (
		<>
			{!hideStoreInventory ? (
				<StoreAvailabilityCard
					isB2B={isB2B}
					bayData={bayData}
					storeAvailability={storeAvailability}
					storeName={storeName as string}
					locationName={locationName}
					showSearchInOtherStore={showSearchInOtherStore}
					productId={productIdsForInventory ?? product?.id}
					isKitOrBundle={isKitOrBundle}
					kitComponents={kitComponents}
					storeAvailProduct={storeAvailProduct as ProductAvailabilityData}
					physicalStoreId={physicalStoreId}
				/>
			) : null}
			{showExtendCatalog ? <ExtendedCatalogCard /> : null}
		</>
	);
};
